import clsx from "clsx";
import { FC, MouseEvent, useCallback } from "react";
import { useCopyToClipboard } from "usehooks-ts";

import { SectionRole } from "../../../shared/types/roles";
import { SECTION_ROLE_TO_ICON } from "../home/admin/users/UserRoleIcons";

import { SectionUserType, UserType } from "components/server-types";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { Menu, MenuItem, MenuRadioGroup, SubMenu } from "mds/components/Menu";
import { showConfirmationModal } from "mds/components/Modal";
import { TooltipTrigger } from "mds/components/TooltipTrigger";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import {
  CircleDisabledIcon,
  DotMenuVerticalIcon,
  LetterIcon,
  MicrophoneOffIcon,
  PersonSingleIcon,
  ViewPersonIcon,
} from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  selectClassUserIdByUserId,
  selectCurrentClassUserId,
  selectCurrentCourseCode,
  selectCurrentSectionIsArchived,
  selectCurrentSectionTitle,
  selectIsOrgUserDisabled,
  selectSectionUserByUserId,
  userSelectors,
} from "store/selectors";
import { toastLocalizedOperationCatcher } from "utils/alerts";
import { useAv } from "utils/av";
import {
  useMuteParticipantTools,
  usePinParticipantTools,
} from "utils/hooks/use-virtual-participant-tools";
import { SECTION_ROLES, getSectionResourceRole } from "utils/section";
import { createImpersonationUrl, getUserFullName } from "utils/user-utils";

const handleDisableUser = (
  user: UserType,
  sectionUser: SectionUserType,
  sectionTitle: string | null,
  courseCode: string | null,
) => {
  const resourceRole = getSectionResourceRole(sectionUser.role);
  const role = t(`roles.section_${resourceRole}`);
  const name = getUserFullName(user);
  const sectionName = t("common.course_code_and_title", {
    code: courseCode,
    title: sectionTitle,
  });

  const modalAction = sectionUser.is_disabled ? "grant" : "restrict";

  return showConfirmationModal({
    title: t(`user_options_menu.role.${modalAction}_title`, { role }),
    labelText: t(`user_options_menu.role.${modalAction}_label`, {
      name,
      course: sectionName,
    }),
    confirmButtonText: t(`user_options_menu.role.${modalAction}_button_text`),
    confirmButtonKind: sectionUser.is_disabled ? "accent" : "caution",
    onConfirm: () => {
      storeApi.section_users.partial_update({
        id: sectionUser.id,
        is_disabled: !sectionUser.is_disabled,
      });
    },
  });
};

type SectionUserOptionsMenuProps = {
  userId: string;
};

export const SectionUserOptionsMenu: FC<SectionUserOptionsMenuProps> = ({ userId }) => {
  const [, copyToClipboard] = useCopyToClipboard();
  const { canAuthorSection, canImpersonateUser, canEditEnrollment } = useCapabilities();
  const user = useAppSelector((state) => userSelectors.selectById(state, userId));
  const sectionUser = useAppSelector((state) => selectSectionUserByUserId(state, userId));
  const classUserId = useAppSelector((state) => selectClassUserIdByUserId(state, userId));
  const currentSectionTitle = useAppSelector(selectCurrentSectionTitle);
  const currentCourseCode = useAppSelector(selectCurrentCourseCode);
  const isArchived = useAppSelector(selectCurrentSectionIsArchived);
  const isOrgUserDisabled = useAppSelector((state) => selectIsOrgUserDisabled(state, userId));
  const currentClassUserId = useAppSelector(selectCurrentClassUserId);
  const { isInVirtualClass } = useAv();
  const { onPinClassUserIdList, pinParticipantDisabledText } = usePinParticipantTools(classUserId);
  const { onMuteParticipant, muteParticipantDisabledText } = useMuteParticipantTools(
    userId,
    classUserId,
  );

  const handleChangeRole = useCallback(
    (newRole: SectionRole) => {
      if (!sectionUser) return;

      storeApi.section_users
        .partial_update({
          id: sectionUser.id,
          role: newRole,
        })
        .catch(
          toastLocalizedOperationCatcher("update_failure", {
            item: t("glossary.user_role"),
          }),
        );
    },
    [sectionUser],
  );

  if (!user || !sectionUser) return null;

  const { role: currentRole, is_disabled } = sectionUser;
  const createdInLms = sectionUser.creation_source === "lms";
  const { email } = user;
  const isOtherParticipant = currentClassUserId !== classUserId;
  const canEditOtherParticipant =
    isInVirtualClass && canAuthorSection && !is_disabled && !!classUserId && isOtherParticipant;

  const MenuButton = (
    <SquareIconButton
      aria-label={t("common.options")}
      kind="ghost"
      size="xs"
      onClick={(e: MouseEvent) => {
        e.stopPropagation();
      }}
    >
      <DotMenuVerticalIcon />
    </SquareIconButton>
  );

  const LmsManagedTooltip = <TooltipTrigger content={t("user_options_menu.lms_managed_user")} />;
  const OrgUserDisabledTooltip = (
    <TooltipTrigger content={t("user_options_menu.org_user_disabled")} />
  );
  const FeatureParticipantDisabledTooltip = pinParticipantDisabledText ? (
    <TooltipTrigger content={pinParticipantDisabledText} />
  ) : null;

  return (
    <Menu
      direction="left"
      menuButton={MenuButton}
      menuClassName="min-w-[180px]"
      onClick={(e) => e.stopPropagation()}
    >
      {canEditOtherParticipant && (
        <MenuItem
          className={clsx("flex w-full items-center justify-between", {
            disabled: !!pinParticipantDisabledText,
          })}
          disabled={!!pinParticipantDisabledText}
          eventName="User Options - View participant"
          onClick={() => {
            if (classUserId) onPinClassUserIdList([classUserId]);
          }}
        >
          <IconText iconStart={<ViewPersonIcon />} text={t("user_options_menu.view_participant")} />
          {FeatureParticipantDisabledTooltip}
        </MenuItem>
      )}

      {canEditOtherParticipant && (
        <MenuItem
          className={clsx("flex w-full items-center justify-between", {
            disabled: !!muteParticipantDisabledText,
          })}
          disabled={!!muteParticipantDisabledText}
          eventName="User Options - Mute participant"
          onClick={onMuteParticipant}
        >
          <IconText
            iconStart={<MicrophoneOffIcon />}
            text={t("user_options_menu.mute_participant")}
          />
          {muteParticipantDisabledText && <TooltipTrigger content={muteParticipantDisabledText} />}
        </MenuItem>
      )}

      <MenuItem
        className="h-auto"
        eventName="User Options - Send email"
        eventProps={{ userEmail: email, userId }}
        href={`mailto:${email}`}
        target="_blank"
        allowPropagation
      >
        <div className="flex flex-col gap-1">
          <IconText iconStart={<LetterIcon />} text={t("user_options_menu.email_user")} />
          <div className="ml-6 block">{email}</div>
        </div>
      </MenuItem>

      {canImpersonateUser && (
        <MenuItem
          eventName="User Options - Impersonate user"
          eventProps={{ userEmail: email, userId }}
          onClick={() => {
            createImpersonationUrl(userId).then(copyToClipboard);
          }}
        >
          <IconText
            iconStart={<PersonSingleIcon />}
            text={t("user_options_menu.impersonate_user")}
          />
        </MenuItem>
      )}

      {canEditEnrollment && (
        <SubMenu
          className={clsx({ disabled: createdInLms || isArchived })}
          disabled={createdInLms || isArchived}
          label={
            <div className="flex w-full items-center justify-between gap-1">
              <IconText
                iconStart={SECTION_ROLE_TO_ICON[currentRole]}
                text={t("user_options_menu.edit_role")}
              />
              {createdInLms && LmsManagedTooltip}
            </div>
          }
        >
          <MenuRadioGroup
            value={currentRole}
            onRadioChange={(e) => handleChangeRole(e.value as SectionRole)}
          >
            {SECTION_ROLES.map((role) => (
              <MenuItem
                eventName="User Options - Change user role"
                eventProps={{
                  userId,
                  userEmail: user.email,
                  userGivenName: user.first_name,
                  userFamilyName: user.last_name,
                  newRole: role,
                }}
                key={role}
                type="radio"
                value={role}
              >
                <IconText
                  iconStart={SECTION_ROLE_TO_ICON[role]}
                  text={t(`roles.section_${getSectionResourceRole(role)}`)}
                />
              </MenuItem>
            ))}
          </MenuRadioGroup>
        </SubMenu>
      )}

      {canEditEnrollment && (
        <MenuItem
          className={clsx("flex w-full items-center justify-between", {
            disabled: createdInLms || isOrgUserDisabled || isArchived,
          })}
          disabled={createdInLms || isOrgUserDisabled || isArchived}
          eventName={is_disabled ? "User Options - Restore user" : "User Options - Disable user"}
          eventProps={{
            userId,
            userEmail: user.email,
            userGivenName: user.first_name,
            userFamilyName: user.last_name,
          }}
          kind={!is_disabled ? "destructive" : undefined}
          onClick={() =>
            handleDisableUser(user, sectionUser, currentSectionTitle, currentCourseCode)
          }
        >
          <IconText
            iconStart={
              !is_disabled ? <CircleDisabledIcon className="text-red" /> : <PersonSingleIcon />
            }
            text={is_disabled ? t("user_actions.enable") : t("user_actions.disable")}
          />
          {createdInLms && LmsManagedTooltip}
          {isOrgUserDisabled && OrgUserDisabledTooltip}
        </MenuItem>
      )}
    </Menu>
  );
};
